import type { InjectedContentProps, StyledInjectedContent } from '../../../types/algolia';

import { useStats } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { media } from '../../shared/core/styles';
import { BaseStyled } from '~/components/unmanaged/styles/BaseStyled';
import { BaseStyledModifier, ColorScheme } from '~/components/unmanaged/styles/BaseStyledModifiers';
import { BaseStyledStyling } from '~/components/unmanaged/styles/BaseStyledStyling';
import { DEFAULT_GRID_COLUMNS } from '~/utilities/constants/plpGrid';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';

const none = css`
  display: none;
`;

const mobileCSS = css<StyledInjectedContent>`
  display: flex;
  grid-row: ${({ $mobileRow }) => $mobileRow};
  grid-column: ${({ $mobileColumn }) => $mobileColumn};
`;

const tabletCSS = css<StyledInjectedContent>`
  display: flex;
  grid-row: ${({ $tabletRow }) => $tabletRow};
  grid-column: ${({ $tabletColumn }) => $tabletColumn};
`;

const desktopCSS = css<StyledInjectedContent>`
  display: flex;
  grid-row: ${({ $desktopRow }) => $desktopRow};
  grid-column: ${({ $desktopColumn }) => $desktopColumn};
`;

const renderGridMinHeightCSS = (span: number) => `
  --calc-grid-spacing: calc( ((var(--grid-column-gap) * ${
    span - 1
  } + var(--grid-inline-margin) * 2) + var(--side-nav-width, 0px)) / ${span});
  .contentBlock {
      min-height: calc( (${
        100 / span
      }vw - var(--calc-grid-spacing))  / 0.75 + var(--product-data-height));
  }`;

const ModifierCSS = {
  Split: css`
    --ci-body-width: 100%;
    --ci-body-justify: start;

    :where(.contentBlock-body) {
      width: var(--ci-body-width, 100%);
      justify-self: var(--ci-body-justify, start);
    }

    @media ${({ theme }) => media(theme).between('sm', 'lg')} {
      &:where(.contentBlock--smSplitLeft),
      &:where(.contentBlock--smSplitRight) {
        --ci-body-width: 50%;
      }

      &:where(.contentBlock--smSplitRight) {
        --ci-body-justify: end;
      }
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      &:where(.contentBlock--mdSplitLeft),
      &:where(.contentBlock--mdSplitRight) {
        --ci-body-width: 50%;
      }

      &:where(.contentBlock--mdSplitRight) {
        --ci-body-justify: end;
      }
    }
  `,
  JeansFitGuide: css`
    &:where(.contentBlock--xsNewJeansStyles) {
      --contentBlock-shadow-opacity: 0;

      @media ${({ theme }) => media(theme).lessThan('sm')} {
        :where(.contentBlock-picture) {
          aspect-ratio: 1/1;
        }

        :where(.contentBlock-body) {
          grid-area: 2/1;
          background: linear-gradient(
            var(--deg, 0deg),
            var(--colorStart, #303030) 0%,
            var(--colorEnd, #303030) 100%
          );
        }
      }
    }
  `,
};

const S = {
  GridItem: styled.article<StyledInjectedContent>`
    ${BaseStyledStyling.BoxSizingBorder}

    position: relative;
    z-index: 0;
    overflow: hidden;

    ${BaseStyledStyling.ParallaxSafeOverflow}

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      ${({ $shouldShowMobile }) => ($shouldShowMobile ? mobileCSS : none)}

      ${({ $shouldShowMobile, $mobileSpan, $mobileOnLastRow }) =>
        $shouldShowMobile &&
        ($mobileSpan === 2 || $mobileOnLastRow) &&
        `
        --product-data-height: 80px;
        ${renderGridMinHeightCSS(2)}
      `}
    }

    @media ${({ theme }) => media(theme).between('sm', 'lg')} {
      ${({ $shouldShowTablet }) => ($shouldShowTablet ? tabletCSS : none)}

      ${({ $shouldShowTablet, $tabletSpan, $tabletOnLastRow }) =>
        $shouldShowTablet &&
        ($tabletSpan === 3 || $tabletOnLastRow) &&
        `
        --product-data-height: 48px;
        ${renderGridMinHeightCSS(3)}
      `}
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      ${({ $shouldShowDesktop }) => ($shouldShowDesktop ? desktopCSS : none)}

      ${({ $shouldShowDesktop, $desktopSpan, $desktopOnLastRow }) =>
        $shouldShowDesktop &&
        ($desktopSpan === 4 || $desktopOnLastRow) &&
        `
        --product-data-height: 48px;
        ${renderGridMinHeightCSS(4)}
      `}
    }

    ${BaseStyled.ContentBlock}

    :where(.contentBlock) {
      ${BaseStyledModifier.Layout}
      ${BaseStyledModifier.Promo}
      ${BaseStyledModifier.Visible}
      --contentBlock-align-items: flex-end;
      --contentBlock-body-padding: 16px;
      --ci-title-font-size: var(--ds-heading-sm, 24px);
      --ci-subTitle-font-size: var(--ds-heading-sm, 24px);

      @media ${({ theme }) => media(theme).greaterThan('tablet')} {
        --ci-title-font-size: var(--ds-heading-md, 32px);
        --ci-subTitle-font-size: var(--ds-heading-md, 32px);
      }

      @media ${({ theme }) => media(theme).greaterThan('xxl')} {
        --ci-title-font-size: clamp(32px, calc(32 * (100 / 1680) * 1vw), 64px);
        --ci-subTitle-font-size: clamp(32px, calc(32 * (100 / 1680) * 1vw), 64px);
      }

      &:where(:has(.lockup-container)) {
        ${ColorScheme.light}
        --contentBlock-align-items: start;
        --contentBlock-text-align: left;
        --contentBlock-body-padding: 32px;
      }

      &:where(.contentBlock--buttonLink, .contentBlock--buttonUnderline) {
        --contentBlock-body-padding: initial;
      }

      :where(.contentBlock-body-title),
      :where(.contentBlock-body-subTitle) {
        ${BaseStyledStyling.HeadingStyling}
        font-size: var(--ci-title-font-size, 24px);
      }

      :where(.contentBlock-body-subTitle) {
        font-size: var(--ci-subTitle-font-size, 24px);
      }

      :where(.contentBlock-body-text) {
        font-weight: 600;
        margin-top: 8px;
        text-wrap: pretty;
      }

      :where(.download-buttons) {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      ${ModifierCSS.Split}
      ${ModifierCSS.JeansFitGuide}
    }

    /* TODO: FS-3437 - PLP Moodbanners remove code for suppressing inline styling */
    .contentBlock-body-link {
      margin: 0 !important;
      line-height: 1.1 !important;
    }
  `,
};

export const InjectedContentItem = ({
  positionDesktop = 0,
  positionTablet = 0,
  positionMobile = 0,
  spanDesktop = 0,
  spanTablet = 0,
  spanMobile = 0,
  children,
}: InjectedContentProps) => {
  const { nbHits, nbPages } = useStats();
  const { contentSize = 0 } = useProductListContext();
  const { siteType } = useAppContext();

  const range = nbHits + contentSize;

  const defaultGridColumns = DEFAULT_GRID_COLUMNS[siteType];
  const desktopGridItemsPerRow =
    Number(localStorage.getItem(`plp-columns-desktop`)) || defaultGridColumns.desktop;
  const tabletGridItemsPerRow =
    Number(localStorage.getItem(`plp-columns-tablet`)) || defaultGridColumns.tablet;
  const mobileGridItemsPerRow =
    Number(localStorage.getItem(`plp-columns-mobile`)) || defaultGridColumns.mobile;

  type GridConfig = {
    position: number;
    span: number;
    gridItemsPerRow: number;
  };

  const calcGrid = ({ position, span, gridItemsPerRow }: GridConfig) => {
    const row = Math.ceil(position / gridItemsPerRow);
    const column = position % gridItemsPerRow || gridItemsPerRow;
    const gridColumn = `${column} / span ${span}`;

    const onLastRow =
      (column === 1 && position === range - span) ||
      (column === 1 && Math.ceil(range / gridItemsPerRow) === row);
    const canFit =
      column + (span - 1) <= gridItemsPerRow && (nbPages > 1 ? true : row * column <= range);
    const shouldShow = position <= range - (span - 1) && canFit;

    return { row, gridColumn, onLastRow, shouldShow };
  };

  const gridConfig = (gridItemsPerRow: number): GridConfig => {
    switch (gridItemsPerRow) {
      case 4:
        return { position: positionDesktop, span: spanDesktop, gridItemsPerRow };
      case 3:
        return { position: positionTablet, span: spanTablet, gridItemsPerRow };
      case 2:
        return { position: positionMobile, span: spanMobile, gridItemsPerRow };
      default:
        return { position: positionMobile, span: 1, gridItemsPerRow };
    }
  };

  const desktopGrid = calcGrid(gridConfig(desktopGridItemsPerRow));
  const tabletGrid = calcGrid(gridConfig(tabletGridItemsPerRow));
  const mobileGrid = calcGrid(gridConfig(mobileGridItemsPerRow));

  return (
    <S.GridItem
      // desktop
      $desktopRow={desktopGrid.row}
      $desktopColumn={desktopGrid.gridColumn}
      $desktopSpan={spanDesktop}
      $desktopOnLastRow={desktopGrid.onLastRow}
      $shouldShowDesktop={desktopGrid.shouldShow}
      // tablet
      $tabletRow={tabletGrid.row}
      $tabletColumn={tabletGrid.gridColumn}
      $tabletSpan={spanTablet}
      $tabletOnLastRow={tabletGrid.onLastRow}
      $shouldShowTablet={tabletGrid.shouldShow}
      // mobile
      $mobileRow={mobileGrid.row}
      $mobileColumn={mobileGrid.gridColumn}
      $mobileSpan={spanMobile}
      $mobileOnLastRow={mobileGrid.onLastRow}
      $shouldShowMobile={mobileGrid.shouldShow}
      // legacy fallback
      className="injection-content"
      data-span-mobile={`${spanMobile}`}
      data-span-tablet={`${spanTablet}`}
      data-span-desktop={`${spanDesktop}`}
      data-spans={`${spanDesktop},${spanTablet},${spanMobile}`}
    >
      {children}
    </S.GridItem>
  );
};
